import { CreationToolError } from '@App/app/shared/exceptions/creation-tool-error';
import { CustomScene } from '@App/app/entities/viewer/custom-scene.model';
import { Plane, Quaternion, Ray, Tools, Vector3 } from 'babylonjs';
import { median } from '@App/app/shared/utils/utils';
import { modelMeshPredicate } from '../../../helpers/layers-helpers';

export const findCenterEdges = (
  scene: CustomScene,
  centers: [Vector3, Vector3],
  direction: Vector3,
): [Vector3, Vector3] => {
  const edges: [Vector3, Vector3] = [Vector3.Zero(), Vector3.Zero()];
  const ray1 = new Ray(centers[0], direction, Vector3.Distance(centers[0], centers[1]) * 2);
  const ray2 = new Ray(
    centers[1],
    direction.negate(),
    Vector3.Distance(centers[0], centers[1]) * 2,
  );
  [ray1, ray2].forEach((ray, i) => {
    const pick = scene.pickWithRay(ray, modelMeshPredicate);
    if (pick?.pickedMesh && pick.pickedPoint) {
      edges[i] = pick.pickedPoint.clone();
    }
  });
  return edges;
};

export const findEdges = (
  scene: CustomScene,
  centers: [Vector3, Vector3],
  direction: Vector3,
  radius: number,
): [Vector3, Vector3] => {
  const findOriginAndCross = (edge: Vector3, i: number, n: number) => {
    const origin =
      i === 0
        ? edge.add(direction.scale(n * 0.001))
        : edge.add(direction.negate().scale(n * 0.001));
    const cross = origin.normalizeToNew().cross(direction).normalizeToNew();
    return [origin, cross];
  };

  const calculateFinalEdge = (edge: Vector3, i: number, n: number) => {
    return i === 0
      ? edge.add(direction.scale((n - 1) * 0.001))
      : edge.add(direction.negate().scale((n - 1) * 0.001));
  };

  const edges: [Vector3, Vector3] = [Vector3.Zero(), Vector3.Zero()];
  const centerEdges = findCenterEdges(scene, centers, direction);
  if (centerEdges.some((edge) => !edge)) {
    throw new CreationToolError('Mesh edges not find');
  }
  centerEdges.forEach((edge, i) => {
    let isModelPicked = true;
    let n = 1;
    while (isModelPicked) {
      const [origin, cross] = findOriginAndCross(edge, i, n);
      const hitsModel = sampleCrossSectionHitsModel(scene, origin, cross, direction, radius);
      isModelPicked = hitsModel && n < 100;
      n++;
    }
    edges[i] = calculateFinalEdge(edge, i, n);
  });
  return edges;
};

const sampleCrossSectionHitsModel = (
  scene: CustomScene,
  origin: Vector3,
  cross: Vector3,
  direction: Vector3,
  radius: number,
): boolean => {
  let pickedPoints = 0;
  for (let j = 0; j < 12; j++) {
    const rotatedCross = new Vector3(0, 0, 0);
    cross.rotateByQuaternionToRef(
      Quaternion.RotationAxis(direction, Tools.ToRadians(j * 15)),
      rotatedCross,
    );
    const position = origin.add(rotatedCross.scale(radius * 1.1));
    const ray = new Ray(position, rotatedCross.negate(), radius * 2.2);
    const pick = scene.pickWithRay(ray, modelMeshPredicate);
    if (pick?.pickedMesh && pick.pickedPoint) {
      pickedPoints += 1;
    }
    if (pickedPoints > 1) {
      break;
    }
  }
  return pickedPoints > 0;
};

export const calculateRadiusOuter = (
  scene: CustomScene,
  centers: [Vector3, Vector3],
  direction: Vector3,
): number => {
  const radiuses: number[] = [];
  for (let i = 0; i < 6; i++) {
    const origin = centers[0].add(
      direction.scale((Vector3.Distance(centers[0], centers[1]) / 5) * i),
    );
    const cross = origin.normalizeToNew().cross(direction).normalizeToNew();
    for (let j = 0; j < 12; j++) {
      const rotatedCross = new Vector3(0, 0, 0);
      cross.rotateByQuaternionToRef(
        Quaternion.RotationAxis(direction, Tools.ToRadians(j * 30)),
        rotatedCross,
      );
      const rayRotated = new Ray(
        origin,
        rotatedCross,
        Vector3.Distance(centers[0], centers[1]) * 2,
      );
      const pick = scene.pickWithRay(rayRotated, modelMeshPredicate);
      if (pick?.pickedPoint && pick.pickedMesh) {
        radiuses.push(Vector3.Distance(pick.pickedPoint, origin));
      }
    }
  }
  return median(radiuses);
};

export const calculateRadiusInner = (
  centers: [Vector3, Vector3],
  inners: [Vector3[], Vector3[]] = [[], []],
  direction: Vector3,
): number => {
  const planes = [
    Plane.FromPositionAndNormal(centers[0], direction),
    Plane.FromPositionAndNormal(centers[1], direction),
  ];
  const radiuses = inners.flatMap((positions, i) => {
    return positions.map((position) => {
      const projected = position
        .clone()
        .projectOnPlane(planes[i], position.add(i === 1 ? direction : direction.negate()));
      const distance = Vector3.Distance(projected, centers[i]);
      return distance;
    });
  });
  return radiuses.reduce((a, b) => a + b, 0) / radiuses.length;
};
